// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type PartCatalog, useEvolvePost } from '@evolvemep/api-ts';
import { Button, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';

type Props = {
  onCatalogCreated: (partCatalog: PartCatalog) => void;
  disabled?: boolean;
};

const CreateCatalogForm = ({ onCatalogCreated }: Pick<Props, 'onCatalogCreated'>) => {
  const { apiCall: createCatalogApiCall } = useEvolvePost('shop/partCatalog');
  const form = useForm<{ name: string }>({
    initialValues: { name: '' },
    validate: { name: (v) => !v },
  });
  const onSubmit = async (values: typeof form.values) => {
    await createCatalogApiCall(values).then(async (c) => {
      onCatalogCreated(c);
      notifications.show({
        title: 'Successfully created',
        message: `Catalog ${c.name} created.`,
        color: 'green',
      });
    });
  };

  useFormModal(form, onSubmit);

  return (
    <TextInput label="Catalog Name" required maxLength={64} autoFocus data-autofocus {...form.getInputProps('name')} />
  );
};

export const CreateCatalogModal = ({ onCatalogCreated, disabled }: Props) => {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <Button
        onClick={() => setOpened(true)}
        variant="light"
        size="sm"
        disabled={disabled}
        leftSection={<EvolveIcon icon="Add" size="sm" />}
      >
        New catalog
      </Button>
      <FormModal
        title="New catalog"
        opened={opened}
        onClose={() => setOpened(false)}
        confirmationText="Create"
        type="submit"
      >
        <CreateCatalogForm onCatalogCreated={onCatalogCreated} />
      </FormModal>
    </>
  );
};
